import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/root-state';

import { fetchPropertyEvents } from '@/features/properties/propertiesThunks';
import { TabLoadBanner } from './shared/TabLoadBanner';
import {
  selectPropertyEvents,
  selectPropertyEventsCanLoadMore,
  selectPropertyEventsError,
  selectPropertyEventsLoading,
  selectPropertyEventsNextToken,
  selectPropertyEventsTotal,
} from '@/features/properties/propertiesSlice';
import type { IPropertyEvent } from '@/domain/entities/property-event';
import { formatUnixDate } from '@/lib';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type EventsTabProps = {
  propertyId: string;
};

function shortId(skOrId?: string) {
  if (!skOrId) return '';
  return skOrId.length > 14 ? `${skOrId.slice(0, 8)}…${skOrId.slice(-4)}` : skOrId;
}

function formatMetaValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);

  // Dates sometimes come in as unix seconds inside meta
  // (we won't auto-convert unless you want; keeping generic)
  if (v instanceof Date) return v.toISOString();

  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function prettifyKey(key: string): string {
  // buyerEmail -> Buyer Email, initiated_at -> Initiated At
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function EventMetaTable(props: { data?: Record<string, unknown> | null }) {
  const { data } = props;
  const [expanded, setExpanded] = useState(false);

  const entries = useMemo(() => {
    if (!data) return [];
    return Object.entries(data)
      .filter(([, v]) => v !== undefined) // hide undefined
      .sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  if (!entries.length) return null;

  // show a few rows by default, expandable
  const visible = expanded ? entries : entries.slice(0, 6);
  const canExpand = entries.length > 6;

  return (
    <div className="bg-muted/20 space-y-2 rounded-md border p-3">
      <div className="text-muted-foreground text-xs font-medium">Metadata</div>

      <div className="space-y-2">
        {visible.map(([k, v]) => {
          const rendered = formatMetaValue(v);
          const isMultiline = rendered.includes('\n') || rendered.length > 80;

          return (
            <div key={k} className="grid grid-cols-12 gap-2">
              <div className="text-muted-foreground col-span-4 text-xs font-medium break-words">{prettifyKey(k)}</div>
              <div className="col-span-8">
                {isMultiline ? (
                  <pre className="bg-background/60 rounded border p-2 text-xs break-words whitespace-pre-wrap">
                    {rendered}
                  </pre>
                ) : (
                  <div className="text-sm break-words">{rendered || '—'}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canExpand ? (
        <div className="pt-1">
          <Button variant="outline" size="sm" onClick={() => setExpanded((x) => !x)}>
            {expanded ? 'Show less' : `Show all (${entries.length})`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function PropertyEventCard(props: { event: IPropertyEvent }) {
  const { event } = props;

  const time = formatUnixDate(event.eventTime);

  return (
    <Card className="shadow-sm">
      <CardHeader className="space-y-1">
        <CardTitle className="text-base">{event.title ?? 'Property event'}</CardTitle>
        <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <span>{time}</span>
          <span className="font-medium">{event.type}</span>
          <span>Actor: {event.actorUserID}</span>
          {event.actorOrgID ? <span>Org: {event.actorOrgID}</span> : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {event.description ? (
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">Description</Label>
            <div className="text-sm whitespace-pre-wrap">{event.description}</div>
          </div>
        ) : null}

        <EventMetaTable data={event.data ?? null} />

        <div className="text-muted-foreground grid gap-2 text-xs md:grid-cols-3">
          <div>propertyID: {event.propertyID}</div>
          <div>sk: {shortId(event.sk)}</div>
          <div>eventTime: {event.eventTime}</div>
        </div>
      </CardContent>
    </Card>
  );
}

type EventsSortKey = 'time_desc' | 'time_asc' | 'type_asc' | 'actor_asc';

/** Main Component */
export function EventsTab({ propertyId }: EventsTabProps) {
  const dispatch = useAppDispatch();

  // pagination UI state (limit is constant for now)
  const limit = 50;

  // pull per-property state
  const items = useAppSelector((s) => selectPropertyEvents(s, propertyId));
  const isLoading = useAppSelector((s) => selectPropertyEventsLoading(s, propertyId));
  const error = useAppSelector((s) => selectPropertyEventsError(s, propertyId));
  const nextToken = useAppSelector((s) => selectPropertyEventsNextToken(s, propertyId));
  const canLoadMore = useAppSelector((s) => selectPropertyEventsCanLoadMore(s, propertyId));
  const total = useAppSelector((s) => selectPropertyEventsTotal(s, propertyId));
  const hasFetched = useAppSelector((s: RootState) => {
    const p = s.properties;
    return p.eventsHasFetchedByPropertyId?.[propertyId] ?? false;
  });
  const isAllLoaded = hasFetched && !nextToken && total > 0 ? items.length >= total : hasFetched && !nextToken;

  const [sortKey, setSortKey] = useState<EventsSortKey>('time_desc');

  const sortedItems = useMemo(() => {
    if (!isAllLoaded) return items;

    const arr = items.slice();

    switch (sortKey) {
      case 'time_desc':
        arr.sort((a, b) => (b.eventTime ?? 0) - (a.eventTime ?? 0));
        break;
      case 'time_asc':
        arr.sort((a, b) => (a.eventTime ?? 0) - (b.eventTime ?? 0));
        break;
      case 'type_asc':
        arr.sort((a, b) => (a.type ?? '').localeCompare(b.type ?? ''));
        break;
      case 'actor_asc':
        arr.sort((a, b) => (a.actorUserID ?? '').localeCompare(b.actorUserID ?? ''));
        break;
    }

    return arr;
  }, [items, sortKey, isAllLoaded]);

  // first load
  useEffect(() => {
    if (!propertyId) return;
    if (hasFetched) return;
    void dispatch(fetchPropertyEvents({ propertyID: propertyId, limit }));
  }, [dispatch, propertyId, hasFetched]);

  const onRefresh = () => {
    if (!propertyId) return;
    void dispatch(fetchPropertyEvents({ propertyID: propertyId, limit }));
  };

  const onLoadMore = () => {
    if (!propertyId) return;
    if (!nextToken) return;
    void dispatch(fetchPropertyEvents({ propertyID: propertyId, limit, nextToken }));
  };

  return (
    <div className="space-y-3">
      <TabLoadBanner mode="edit" isLoading={isLoading && !hasFetched} error={error} loadingLabel="Loading events…" />

      <div className="flex items-center justify-between gap-2">
        <div className="text-muted-foreground text-sm">
          {hasFetched ? `${items.length} / ${total} loaded` : 'Not loaded yet'}
        </div>

        <div className="flex items-center gap-2">
          <div className="w-[180px]">
            <Select value={sortKey} onValueChange={(v) => setSortKey(v as EventsSortKey)} disabled={!isAllLoaded}>
              <SelectTrigger>
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="time_desc">Newest first</SelectItem>
                <SelectItem value="time_asc">Oldest first</SelectItem>
                <SelectItem value="type_asc">Type (A→Z)</SelectItem>
                <SelectItem value="actor_asc">Actor (A→Z)</SelectItem>
              </SelectContent>
            </Select>

            {!isAllLoaded ? (
              <div className="text-muted-foreground mt-1 text-[11px]">Sorting enabled after all events load.</div>
            ) : null}
          </div>

          <Button variant="outline" onClick={onRefresh} disabled={isLoading}>
            Refresh
          </Button>
          <Button onClick={onLoadMore} disabled={!canLoadMore}>
            Load more
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        {items.length === 0 && hasFetched && !isLoading ? (
          <div className="text-muted-foreground px-3 py-6 text-sm">No events yet.</div>
        ) : null}

        <div className="space-y-4 px-3 py-4">
          {sortedItems.map((ev) => (
            <PropertyEventCard key={ev.id} event={ev} />
          ))}
        </div>
      </div>

      {nextToken ? (
        <div className="text-muted-foreground text-xs">
          More available. Click <span className="font-medium">Load more</span>.
        </div>
      ) : hasFetched ? (
        <div className="text-muted-foreground text-xs">End of list.</div>
      ) : null}
    </div>
  );
}
